import { useCallback, useEffect, useMemo } from 'react';
import { buildEventDerivedGroupArtistsFromMemberList } from '../../components/common/chat/members/eventParticipantsPanelDataUtils';
import useMemberModalEventState from '../../components/common/chat/members/useMemberModalEventState';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useAdminEventChatParticipants(context) {
  const {
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    EVENT_NOTIFICATION_SILENCE_URL,
    appendVariant,
    buildAssetUrl,
    buildChatMentionCandidates,
    buildEventAdminParticipants,
    buildEventArtistParticipants,
    buildEventConciergeParticipants,
    buildEventParticipantEventPartSummary,
    buildParticipantEventMembershipLabel,
    castingInfo,
    conciergePlanner,
    createEventParticipantsPanelProps,
    createEventProfilePayloadBuilders,
    effectiveGroupEvents,
    fetchJsonSafe,
    findPartLabelByIdRef,
    groupEventParticipantsByPart,
    hasConciergeParticipants,
    isActiveNotificationSilenceWindow,
    isConciergeEvent,
    isSelectedGroupUnified,
    loggedInAdminId,
    mentionSilenceConfirmResolverRef,
    normalizeEventUuidList,
    normalizeMentionTargetId,
    normalizeMentionTargetRole,
    normalizeMentionTargetType,
    normalizePartLabel,
    normalizePartsArray,
    openProfileTarget,
    parsePartIds,
    participantArtistMap,
    participantArtists,
    participantCounts,
    participantGroupEventMap,
    participantGroupEventOrderMap,
    participantSnapshot,
    partsList,
    pickFirstString,
    resolveEventScopedPartMeta,
    selectedEventAdmins,
    selectedEventId,
    selectedEventInfo,
    setMentionSilenceConfirm,
    setProfileTarget,
    setShowMembersModal,
    toNum,
    toastError,
    useEventParticipantPartScope,
  } = context;
    const partsLookup = useMemo(() => {
      const map = new Map();
      const list = Array.isArray(partsList) ? partsList : normalizePartsArray(partsList);
      list.forEach((p) => {
        if (!p) return;
        const pid = toNum(p.id ?? p.part_id ?? p.value);
        if (Number.isFinite(pid)) map.set(pid, p);
      });
      return map;
    }, [partsList]);

    /* ▼▼ 追加：ID→ラベル解決の共通関数（nullセーフ） ▼▼ */
    const findPartLabelById = useCallback((partId) => {
      if (partId == null) return '';
      const num = toNum(partId);
      if (Number.isFinite(num) && partsLookup.has(num)) {
        const meta = partsLookup.get(num);
        return (meta?.label ?? meta?.short_name ?? meta?.name ?? '') || '';
      }
      const list = Array.isArray(partsList) ? partsList : normalizePartsArray(partsList);
      const s = String(partId);
      const hit =
        list.find((p) => String(p?.short_name) === s) ||
        list.find((p) => String(p?.label) === s) ||
        list.find((p) => String(p?.value) === s);
      return hit?.label || hit?.short_name || '';
    }, [partsList, partsLookup]);
    useEffect(() => {
      findPartLabelByIdRef.current = findPartLabelById;
    }, [findPartLabelById]);

    const findPartShortLabelById = useCallback((partId) => {
      if (partId == null) return '';
      const num = toNum(partId);
      if (Number.isFinite(num) && partsLookup.has(num)) {
        const meta = partsLookup.get(num);
        return (meta?.short_name || meta?.label || meta?.name || '') || '';
      }
      return '';
    }, [partsLookup]);
    /* ▲▲ 追加ここまで ▲▲ */

    const partOrderMap = useMemo(() => {
      const map = new Map();
      const list = Array.isArray(partsList) ? partsList : normalizePartsArray(partsList);
      list.forEach((p, idx) => {
        const pid = toNum(p?.id ?? p?.part_id ?? p?.value);
        if (Number.isFinite(pid) && !map.has(pid)) map.set(pid, idx);
      });
      return map;
    }, [partsList]);

    const getPartOrder = useCallback((partId) => {
      const num = toNum(partId);
      if (Number.isFinite(num) && partOrderMap.has(num)) return partOrderMap.get(num);
      return 9999;
    }, [partOrderMap]);

    const castingInfoMap = useMemo(() => {
      const m = new Map();
      (castingInfo || []).forEach((ci) => {
        const id = ci?.id ?? ci?.casting_info_id;
        if (id == null) return;
        m.set(String(id), ci);
      });
      return m;
    }, [castingInfo]);

    const getMainPartLabel = useCallback((artistParts) => {
      const partsArr = parsePartIds(artistParts);
      if (!Array.isArray(partsArr) || partsArr.length === 0) return '';
      const mainPartId = partsArr[0];
      return findPartLabelById(mainPartId);
    }, [findPartLabelById]);

    const getMainPartBadgeLabel = useCallback((artistParts) => {
      const partsArr = parsePartIds(artistParts);
      if (!Array.isArray(partsArr) || partsArr.length === 0) return '';
      return findPartShortLabelById(partsArr[0]);
    }, [findPartShortLabelById]);
    const resolveEventPartLabel = useCallback((artist) => {
      if (!artist) return '';
      const ci = artist.casting_info_id != null ? castingInfoMap.get(String(artist.casting_info_id)) : null;
      const label = normalizePartLabel(
        pickFirstString(ci?.part, artist.partLabel, artist.mainPartLabel, artist.part, artist.mainPart)
      );
      if (label) return label;
      const fallback = normalizePartLabel(getMainPartLabel(artist.artist_parts || artist.parts));
      if (fallback) return fallback;
      return normalizePartLabel(getMainPartBadgeLabel(artist.artist_parts || artist.parts));
    }, [castingInfoMap, getMainPartLabel, getMainPartBadgeLabel]);

    const buildParticipantEventPartSummary = useCallback((artist) => {
      const fallbackPartLabel = resolveEventPartLabel(artist) || 'パート未設定';
      return buildEventParticipantEventPartSummary({
        artist,
        fallbackPartLabel,
        selectedEventId,
        participantGroupEventOrderMap,
        participantGroupEventMap,
        pickFirstString,
        isUnifiedEventGroupChat: isSelectedGroupUnified,
      });
    }, [
      isSelectedGroupUnified,
      participantGroupEventMap,
      participantGroupEventOrderMap,
      resolveEventPartLabel,
      selectedEventId,
    ]);

    const {
      buildArtistProfilePayload,
      buildAdminProfilePayload,
    } = useMemo(
      () =>
        createEventProfilePayloadBuilders({
          pickFirstString,
          normalizePartLabel,
          getMainPartBadgeLabel,
          normalizeEventUuidList,
        }),
      [getMainPartBadgeLabel]
    );

    const adminParticipants = useMemo(() => {
      return buildEventAdminParticipants({
        participantSnapshot,
        selectedEventAdmins,
        selectedEventId,
        pickFirstString,
        normalizeEventUuidList,
        buildParticipantEventMembershipLabel,
        buildAdminProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
      });
    }, [
      participantSnapshot,
      selectedEventAdmins,
      buildAdminProfilePayload,
      buildParticipantEventMembershipLabel,
      selectedEventId,
    ]);

    const conciergeParticipants = useMemo(() => {
      return buildEventConciergeParticipants({
        participantSnapshot,
        conciergePlanner,
        isConciergeEvent,
        selectedEventId,
        pickFirstString,
        normalizeEventUuidList,
        buildParticipantEventMembershipLabel,
        buildArtistProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
      });
    }, [
      participantSnapshot,
      conciergePlanner,
      isConciergeEvent,
      buildArtistProfilePayload,
      buildParticipantEventMembershipLabel,
      selectedEventId,
    ]);

    const {
      participantPartScopeEventUuid,
      setParticipantPartScopeEventUuid,
      participantPartScopeTabs,
    } = useEventParticipantPartScope({
      effectiveGroupEvents,
      selectedEventId,
      pickFirstString,
    });

    const artistParticipants = useMemo(() => {
      return buildEventArtistParticipants({
        participantSnapshot,
        participantArtists,
        participantArtistMap,
        pickFirstString,
        normalizeEventUuidList,
        participantPartScopeEventUuid,
        selectedEventId,
        effectiveGroupEvents,
        getPartOrder,
        buildParticipantEventMembershipLabel,
        buildParticipantEventPartSummary,
        buildArtistProfilePayload,
        buildAvatarUrl: (avatarKey) =>
          avatarKey
            ? appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, avatarKey), 'medium')
            : '',
        resolveArtistParts: (artist) => parsePartIds(artist?.artist_parts || artist?.parts),
        resolveFallbackPartLabel: (artist, mainPartId) =>
          normalizePartLabel(
            resolveEventPartLabel(artist)
            || findPartLabelById(mainPartId)
            || findPartShortLabelById(mainPartId)
            || ''
          ) || 'パート未設定',
        resolveScopedPartMeta: ({ mergedArtist, participantPartScopeEventUuid: scopedEventUuid, isAllPerformancesScope, fallbackPartLabel }) =>
          resolveEventScopedPartMeta({
            mergedArtist,
            participantPartScopeEventUuid: scopedEventUuid,
            isAllPerformancesScope,
            fallbackPartLabel,
            pickFirstString,
            normalizePartLabel,
            castingInfoMap,
          }),
        isUnifiedEventGroupChat: isSelectedGroupUnified,
      });
    }, [
      participantSnapshot,
      participantArtists,
      participantArtistMap,
      buildArtistProfilePayload,
      getPartOrder,
      buildParticipantEventPartSummary,
      castingInfoMap,
      buildParticipantEventMembershipLabel,
      selectedEventId,
      effectiveGroupEvents,
      findPartLabelById,
      findPartShortLabelById,
      isSelectedGroupUnified,
      participantPartScopeEventUuid,
      resolveEventPartLabel,
    ]);

    const artistParticipantsByPart = useMemo(() => {
      return groupEventParticipantsByPart({
        artistParticipants,
        participantPartScopeEventUuid,
        getPartOrder,
      });
    }, [artistParticipants, getPartOrder, participantPartScopeEventUuid]);

    const primaryArtistIds = useMemo(() => {
      if (!participantSnapshot || !Array.isArray(participantSnapshot?.artists)) return null;
      const separatelyListedIds = new Set(
        [
          ...(Array.isArray(participantSnapshot?.manual_chat_artists)
            ? participantSnapshot.manual_chat_artists
            : []),
          ...(Array.isArray(participantSnapshot?.concierge_planners)
            ? participantSnapshot.concierge_planners
            : []),
        ]
          .map((artist) => String(artist?.artist_id || artist?.id || '').trim())
          .filter(Boolean)
      );
      return new Set(
        participantSnapshot.artists
          .map((artist) => String(artist?.artist_id || artist?.id || '').trim())
          .filter((artistId) => artistId && !separatelyListedIds.has(artistId))
      );
    }, [participantSnapshot]);

    const {
      decidedSections: derivedGroupDecidedSections,
      undecidedList: derivedGroupUndecidedList,
    } = useMemberModalEventState({
      open: Boolean(selectedEventId),
      eventId: selectedEventId,
      participantSnapshot,
      parts: partsList,
      castingInfo,
      progress: participantArtists,
      includedArtistIds: primaryArtistIds,
      artistsVisibleOnly: false,
      parsePartIdsFn: parsePartIds,
      pickFirstStringFn: pickFirstString,
      useSnapshotArtistsForUnified: isSelectedGroupUnified,
      isUnifiedEventGroupChat: isSelectedGroupUnified,
    });

    const eventDerivedGroupArtists = useMemo(() => buildEventDerivedGroupArtistsFromMemberList({
      decidedSections: derivedGroupDecidedSections,
      undecidedList: derivedGroupUndecidedList,
      manualChatArtists: participantSnapshot?.manual_chat_artists,
      enrichedArtists: artistParticipants,
      pickFirstString,
    }), [
      artistParticipants,
      derivedGroupDecidedSections,
      derivedGroupUndecidedList,
      participantSnapshot,
      pickFirstString,
    ]);

    const mentionCandidates = useMemo(() => buildChatMentionCandidates({
      admins: adminParticipants,
      artists: [...artistParticipants, ...conciergeParticipants],
      parts: partsList,
      currentUser: { role: 'admin', id: loggedInAdminId },
    }), [adminParticipants, artistParticipants, conciergeParticipants, loggedInAdminId, partsList]);

    const closeMentionSilenceConfirm = useCallback((shouldSend) => {
      const resolver = mentionSilenceConfirmResolverRef.current;
      mentionSilenceConfirmResolverRef.current = null;
      setMentionSilenceConfirm(null);
      if (resolver) resolver(Boolean(shouldSend));
    }, []);

    useEffect(() => () => {
      if (mentionSilenceConfirmResolverRef.current) {
        mentionSilenceConfirmResolverRef.current(false);
        mentionSilenceConfirmResolverRef.current = null;
      }
    }, []);

    const handleBeforeEventChatSend = useCallback(async (summary) => {
      if (
        summary?.type !== 'text' ||
        !selectedEventId ||
        !Array.isArray(summary?.mentions) ||
        summary.mentions.length === 0
      ) {
        return true;
      }

      try {
        const payload = await fetchJsonSafe(
          EVENT_NOTIFICATION_SILENCE_URL(selectedEventId),
          {},
          { label: '通知サイレント設定の確認に失敗しました。' }
        );
        const windows = Array.isArray(payload?.windows) ? payload.windows : [];
        if (!windows.some((windowItem) => isActiveNotificationSilenceWindow(windowItem))) {
          return true;
        }
      } catch (err) {
        toastError(err?.message || '通知サイレント設定の確認に失敗しました。');
        return false;
      }

      return new Promise((resolve) => {
        if (mentionSilenceConfirmResolverRef.current) {
          mentionSilenceConfirmResolverRef.current(false);
        }
        mentionSilenceConfirmResolverRef.current = resolve;
        setMentionSilenceConfirm({
          eventName: selectedEventInfo?.name || selectedEventInfo?.event_name || '選択中のイベント',
        });
      });
    }, [selectedEventId, selectedEventInfo?.event_name, selectedEventInfo?.name, toastError]);

    const mentionProfileTargetMap = useMemo(() => {
      const map = new Map();
      const addTarget = (role, id, payload, type) => {
        const key = String(id || '').trim();
        if (!key || !payload || map.has(`${role}:${key}`)) return;
        map.set(`${role}:${key}`, { type, payload });
      };
      adminParticipants.forEach((admin) => {
        const payload = admin?.payload;
        const fallbackId = String(admin?.key || '').replace(/^admin-/, '');
        addTarget('admin', payload?.id || payload?.admin_user_id || fallbackId, payload, 'admin');
      });
      [...artistParticipants, ...conciergeParticipants].forEach((artist) => {
        const payload = artist?.payload;
        const fallbackId = String(artist?.key || '').replace(/^(artist|concierge)-/, '');
        addTarget('artist', payload?.artist_id || payload?.id || fallbackId, payload, 'artist');
      });
      return map;
    }, [adminParticipants, artistParticipants, conciergeParticipants]);

    const handleMentionClick = useCallback((mention) => {
      const mentionType = normalizeMentionTargetType(mention);
      if (mentionType === 'part') {
        setShowMembersModal(true);
        return;
      }

      const role = normalizeMentionTargetRole(mention);
      const targetId = normalizeMentionTargetId(mention);
      const target =
        mentionProfileTargetMap.get(`${role}:${targetId}`) ||
        mentionProfileTargetMap.get(`artist:${targetId}`) ||
        mentionProfileTargetMap.get(`admin:${targetId}`);
      openProfileTarget(target);
    }, [mentionProfileTargetMap, openProfileTarget]);

    const participantsPanelProps = useMemo(() => createEventParticipantsPanelProps({
      hasConciergeParticipants,
      participantCounts,
      effectiveGroupEvents,
      adminParticipants,
      conciergeParticipants,
      artistParticipantsByPart,
      participantPartScopeTabs,
      participantPartScopeEventUuid,
      onChangeParticipantPartScope: setParticipantPartScopeEventUuid,
      setProfileTarget,
    }), [
      adminParticipants,
      artistParticipantsByPart,
      conciergeParticipants,
      effectiveGroupEvents,
      hasConciergeParticipants,
      participantCounts,
      participantPartScopeEventUuid,
      participantPartScopeTabs,
      setProfileTarget,
      setParticipantPartScopeEventUuid,
    ]);
  return {
    getMainPartLabel,
    buildParticipantEventPartSummary,
    buildArtistProfilePayload,
    buildAdminProfilePayload,
    mentionCandidates,
    closeMentionSilenceConfirm,
    handleBeforeEventChatSend,
    handleMentionClick,
    participantsPanelProps,
    artistParticipants,
    eventDerivedGroupArtists,
  };
}
